import React, { forwardRef, ForwardRefRenderFunction, useContext, useImperativeHandle, useRef } from "react";
import { useSelector } from "react-redux";
import { ChildPost, PostType, PrincipalId } from "../../../RootPost";
import * as Actions from "../../../store/actions";
import { NodeVisibility } from "../../../store/actions";
import { addNodeOperationKey, updateNodeOperationKey } from "../../../store/operation";
import { clearDraft } from "../../shared/Draft";
import RichNodeEditor, { NodeType, RichNodeEditorData, RichNodeEditorHandles } from "./RichNodeEditor";
import { isWorkaround } from "./RichSolution";
import { TicketContext } from "./TicketContext";
import { instance } from "../../../config/serverConfig";

const PRIVATE_COMMENT = "(private comment)";

const addPostDraftName = "post-draft";

export enum NodeEditorOperation {
    Add = "Add",
    Modify = "Modify",
}

const removePrivateCommentMarker = (commentText: string): string => {
    if (!commentText) {
        return commentText;
    }

    if (commentText.indexOf(`**${PRIVATE_COMMENT}**\r\n`) !== -1) {
        return commentText.replace(`**${PRIVATE_COMMENT}**\r\n`, "");
    }

    if (commentText.indexOf(`<strong>${PRIVATE_COMMENT}</strong><br>`) !== -1) {
        return commentText.replace(`<strong>${PRIVATE_COMMENT}</strong><br>`, "");
    }

    return commentText;
}

export interface CommentEditorHandles {
    focus: () => void;
}

export interface CommentEditorProps {
    operation: NodeEditorOperation;
    uiOperationKey?: string;     // id of the UI operation under which this comment is being added/modified. Used to track status and update UI accordingly
    parentNodeId: number;        // Id of parent node to which to add this comment
    nodeToModify?: ChildPost;    // Comment to modify when operation is 'Modify'
    newNodeTrackingId?: string;   // newly generated tracking id to use for submitting the comment
    className?: string;
    disableAddComment?: boolean;
    disableAddSolution?: boolean;
    disableNodeTypeChooser?: boolean;
    placeHolderText?: string;
    title?: JSX.Element | string;
    nodeType?: NodeType;
    disabled?: boolean;
    isSubmitting?: boolean;
    isProvideInfo?: boolean;
    currentUserId: PrincipalId;

    onCancel: () => void;
    onSubmit?: (nodeType: NodeType, commentText: string, visibility: NodeVisibility, newNodeTrackingId: string) => void;
    setNewNodeTrackingId?: (id: string) => void;
}

const CommentEditor: ForwardRefRenderFunction<CommentEditorHandles, CommentEditorProps> = (props: CommentEditorProps, ref): JSX.Element => {
    const ticketDetailsCtx = useContext(TicketContext);
    const ticket = ticketDetailsCtx.ticketDetails.ticket?.data;
    const refRichNodeEditor = useRef<RichNodeEditorHandles>();

    useImperativeHandle(ref, () => ({
        focus: () => {
            refRichNodeEditor.current?.focus();
        }
    }));

    const addNodeOpKey = props.uiOperationKey ?? addNodeOperationKey(ticket?.id, props.parentNodeId);
    const updateNodeOpKey = props.uiOperationKey ?? updateNodeOperationKey(ticketDetailsCtx.ticketDetails?.ticket?.data?.id, props.parentNodeId);

    if ((props.operation === NodeEditorOperation.Add && !props.parentNodeId)
        || (props.operation === NodeEditorOperation.Modify && !props.nodeToModify)) {
        // This should never happen but just in case
        return <div className="error">Comment Editor: Invalid operation</div>
    }

    const forbidAttachFiles = props.operation === NodeEditorOperation.Modify && props.nodeToModify.author.id !== props.currentUserId;

    const allowedNodeTypes: NodeType[] = [];
    let nodeTypeLabel: string = "comment or solution";
    let defaultNodeType: NodeType = null;
    if (props.operation === NodeEditorOperation.Add) {
        const disableAddSolution = props.disableAddSolution;
        if (props.disableAddComment || disableAddSolution) {
            if (!props.disableAddComment) {
                allowedNodeTypes.push(NodeType.comment);
                nodeTypeLabel = "comment";
                defaultNodeType = NodeType.comment;
            }
            if (!disableAddSolution) {
                allowedNodeTypes.push(NodeType.solution);
                allowedNodeTypes.push(NodeType.workaround);
                nodeTypeLabel = "solution";
                defaultNodeType = NodeType.solution;
            }
        } else {
            defaultNodeType = NodeType.comment;
        }
    } else {
        nodeTypeLabel = props.nodeType;
        defaultNodeType = props.nodeType;
    }

    const guidelines = (nodeType: NodeType): JSX.Element => {
        if (nodeType === NodeType.solution || nodeType === NodeType.workaround) {
            return <div>
                <h1>Solution guidelines</h1>
                <div><b>Do:</b> Use this space to post a solution to the problem. Feel free to be as detailed as necessary.</div>
                <div><b>Avoid:</b> Asking questions or responding to other solutions. If you need to ask questions, send a comment instead.</div>
            </div >
        }
        return null;
    }

    const bodyPreviewPrefix = (nodeType: NodeType): string => {
        return "";
    }

    const handleSubmit = (value: RichNodeEditorData): void => {
        if (props.onSubmit) {
            // let parent handle the submission if it wants to
            props.onSubmit(value.nodeType, value.text, value.visibility, props.newNodeTrackingId);
            return;
        }

        if (value.nodeType === NodeType.comment) {
            if (props.operation === NodeEditorOperation.Add) {
                Actions.addComment({
                    item: ticketDetailsCtx.ticketDetails.ticket.data,
                    commentText: value.text,
                    parentId: props.parentNodeId,
                    visibility: value.visibility,
                    isProvideInfo: props.isProvideInfo,
                    newNodeTrackingId: props.newNodeTrackingId,
                    operationKey: addNodeOpKey,
                    includeNativeAttachments: value.includeNativeAttachments
                });
                clearDraft(addPostDraftName);
            }

            if (props.operation === NodeEditorOperation.Modify) {
                Actions.updateComment(ticketDetailsCtx.ticketDetails.ticket.data, props.nodeToModify.id, props.parentNodeId, value.text, props.newNodeTrackingId, updateNodeOpKey);
            }
        }

        if (value.nodeType === NodeType.solution || value.nodeType === NodeType.workaround) {
            if (props.operation === NodeEditorOperation.Add) {
                Actions.addSolutionOrWorkaround(ticketDetailsCtx.ticketDetails.ticket?.data, value.text, props.newNodeTrackingId, updateNodeOpKey, value.nodeType === "workaround");
                clearDraft(addPostDraftName);
            }
            if (props.operation === NodeEditorOperation.Modify) {
                Actions.updateSolutionOrWorkaround(ticketDetailsCtx.ticketDetails.ticket?.data, props.nodeToModify.id, value.text, props.newNodeTrackingId, updateNodeOpKey, isWorkaround(props.nodeToModify));
            }
        }
    }

    let visibility: NodeVisibility | null = null;
    if (props.operation === NodeEditorOperation.Modify && !props.nodeToModify.isPublic) {
        // This is only used to determine whether to show the 'Do not include personal information' message when modifying an existing comment
        // Otherwise it's null and use has too choose the privacy option
        visibility = NodeVisibility.msonly;
    }

    return <RichNodeEditor
        ref={refRichNodeEditor}
        title={props.title ?? (props.operation === NodeEditorOperation.Modify ? `Edit ${nodeTypeLabel}` : `Add ${nodeTypeLabel}`)}
        className={props.className}
        nodeTrackingId={props.operation === NodeEditorOperation.Add ? props.newNodeTrackingId : null}
        setNewNodeTrackingId={props.setNewNodeTrackingId}
        nodeAnswerHubId={props.operation === NodeEditorOperation.Modify ? props.nodeToModify.id : null}
        disabled={props.disabled}
        draftKeyName={props.operation === NodeEditorOperation.Add && addPostDraftName}
        draftLabel="a comment or solution"
        forbidAttachFiles={forbidAttachFiles}
        reasonCantAttachFiles={forbidAttachFiles && "Only author of the comment is allowed to attach files to it"}
        canDismiss
        canChooseVisibility={instance.isInternal ? false : props.operation === NodeEditorOperation.Add}
        defaultVisibility={instance.isInternal ? NodeVisibility.all : visibility}
        canChooseNodeType={props.disableNodeTypeChooser ? false : props.operation === NodeEditorOperation.Add}
        allowedNodeTypes={allowedNodeTypes}
        defaultNodeType={defaultNodeType}
        textPrefix={bodyPreviewPrefix}
        guidelines={guidelines}
        required
        isSubmitting={props.isSubmitting}
        defaultText={props.operation === NodeEditorOperation.Modify ? removePrivateCommentMarker(props.nodeToModify.text) : ""}
        placeHolderText={props.placeHolderText}
        allowNativeAttachments={instance.isInternal ? false : props.operation === NodeEditorOperation.Add && ticket?.type === PostType.problem}
        onDismiss={props.onCancel}
        minChars={10}
        maxChars={40000}
        relevantPersons={ticketDetailsCtx.ticketDetails.relevantPersons}
        onSubmit={handleSubmit}
    />
}

export default forwardRef(CommentEditor);
